'use client'

import { useTranslation } from '@/hooks/client/use-translation'
import type { DirectoryRow } from '@/lib/directory/row'
import { tw } from '@/lib/utils'
import { localizedHref } from '@/lib/utils/localized-href'

import { RowEvidence } from './row-evidence'
import { RowFoot } from './row-foot'
import { RowIdentity } from './row-identity'
import { RowImage } from './row-image'
import { RowSoundScore } from './row-sound-score'
import { RowSpecs } from './row-specs'

interface ReleaseRowProps {
  release: DirectoryRow
  index: number
  isCompared: boolean
  atMax: boolean
  facetLabels: Map<string, string>
  onToggleCompare: (slug: string) => void
}

// One product family as a row, on a four-row grid (photo / text / evidence):
//   1  photo · brand + name      · evidence: expert wheel, then (from md) a
//   2  photo · spec chips        ·   hairline and the SoundScore meter
//   3  SoundScore meter on phones, full width under the chips
//   4  foot: price, review link, lab sample, Compare
// From md the evidence stack spans rows 1–3 and row 3 is `1fr`, so a tall
// stack grows row 3 (empty in the text column) rather than pushing the chips
// away from the name. The meter renders twice, one copy per breakpoint; the
// `hidden` copy is out of the accessibility tree. Every cell is placed by
// explicit grid lines: `col-span-*` writes the `grid-column` shorthand and
// silently overrides `col-start-*`, dropping the cell into auto-placement.
export const ReleaseRow = ({ release, index, isCompared, atMax, facetLabels, onToggleCompare }: ReleaseRowProps) => {
  const { locale } = useTranslation()
  const href = localizedHref(locale, release.path)
  const headingId = `directory-release-${release.slug}`
  const hasScore = release.score != null && release.score > 0
  // With no rating at all the "Not yet tested" chip says it once; a meter
  // reading "Not tested" beneath would only repeat it.
  const showSoundScore = hasScore || (release.soundScore != null && release.soundScore > 0)

  return (
    <li>
      <article
        data-directory-row
        data-selected={isCompared}
        aria-labelledby={headingId}
        className={tw(
          'grid grid-cols-[72px_minmax(0,1fr)_auto] gap-x-3 gap-y-3 rounded-2xl border bg-white p-4 transition-colors md:grid-cols-[112px_minmax(0,1fr)_224px] md:grid-rows-[auto_auto_1fr_auto] md:gap-x-6 md:p-5',
          isCompared ? 'border-navy ring-navy ring-1' : 'border-navy/10 hover:border-navy/30'
        )}
      >
        <RowImage image={release.image} href={href} eager={index < 3} />
        <RowIdentity id={headingId} brandName={release.brandName} fullName={release.fullName} href={href} />
        <div className="col-start-3 row-start-1 self-start justify-self-end md:row-[1/4] md:w-full md:justify-self-start">
          <RowEvidence score={release.score} slug={release.slug} />
          {showSoundScore ? (
            <RowSoundScore score={release.soundScore} className="border-navy/10 mt-3 hidden border-t pt-4 md:block" />
          ) : null}
        </div>
        <div className="col-[2/4] row-start-2 min-w-0 md:col-[2/3]">
          <RowSpecs release={release} facetLabels={facetLabels} />
        </div>
        {showSoundScore ? (
          <RowSoundScore score={release.soundScore} className="col-[2/4] row-start-3 md:hidden" />
        ) : null}
        <RowFoot
          release={release}
          href={href}
          isCompared={isCompared}
          atMax={atMax}
          onToggleCompare={onToggleCompare}
        />
      </article>
    </li>
  )
}
